import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import { OrganizationMarketplaceServicesInfo } from '@spike/marketplace-model';
import { showError } from '@spike/notifications-action';
import Button from 'components/UI/V3/Button/Button';
import { ChipSelector, Element } from 'components/Service/ChipSelector';
import {
    buildSelectedStaffElements,
    buildServiceStaffFromMarketplaces,
    buildStaffElements
} from 'components/Service/Staff/utils/staffElements';
import Service, { StaffMember } from 'model/Service';
import styles from './TrainingStaffStep.module.scss';

interface TrainingStaffStepProps {
    marketplaces: Service['marketplaces'];
    organizationServicesInfo: Array<OrganizationMarketplaceServicesInfo>;
    onComplete: (staff: Array<StaffMember>, marketplaces: Service['marketplaces']) => void;
    isLoading?: boolean;
}

export const TrainingStaffStep: React.FC<TrainingStaffStepProps> = ({
    marketplaces: initialMarketplaces,
    organizationServicesInfo,
    onComplete,
    isLoading = false
}) => {
    const dispatch = useDispatch();
    const [marketplaces, setMarketplaces] = useState<Service['marketplaces']>(() => initialMarketplaces);
    const [expandedMarketplaceIds, setExpandedMarketplaceIds] = useState<Array<number>>([]);

    useEffect(() => {
        setMarketplaces(prev =>
            initialMarketplaces.map(marketplace => {
                const existing = prev.find(mp => mp.marketplaceId === marketplace.marketplaceId);
                return {
                    ...marketplace,
                    taxes: existing?.taxes ?? marketplace.taxes,
                    staffIds: existing?.staffIds ?? marketplace.staffIds,
                    roomIds: marketplace.roomIds ?? existing?.roomIds ?? [],
                    roomUnitIds: marketplace.roomUnitIds ?? existing?.roomUnitIds ?? [],
                    vehicleIds: marketplace.vehicleIds ?? existing?.vehicleIds ?? []
                };
            })
        );
    }, [initialMarketplaces]);

    const selectedMarketplaces = useMemo(
        () =>
            marketplaces
                .map(serviceMarketplace => {
                    const servicesInfo = organizationServicesInfo.find(
                        info => info.marketplace.id === serviceMarketplace.marketplaceId
                    );

                    if (!servicesInfo) {
                        return null;
                    }

                    return {
                        serviceMarketplace,
                        servicesInfo
                    };
                })
                .filter(
                    (
                        marketplace
                    ): marketplace is {
                        serviceMarketplace: Service['marketplaces'][number];
                        servicesInfo: OrganizationMarketplaceServicesInfo;
                    } => marketplace !== null
                )
                .sort((left, right) =>
                    left.servicesInfo.marketplace.name
                        .toLocaleLowerCase()
                        .localeCompare(right.servicesInfo.marketplace.name.toLocaleLowerCase())
                ),
        [organizationServicesInfo, marketplaces]
    );

    const hasActiveStaffInOrganization = useMemo(
        () => organizationServicesInfo.some(info => info.staff.some(member => member.active)),
        [organizationServicesInfo]
    );

    useEffect(() => {
        if (selectedMarketplaces.length === 0) {
            return;
        }

        const shouldAutoLoad = selectedMarketplaces.some(
            ({ serviceMarketplace, servicesInfo }) =>
                serviceMarketplace.staffIds.length === 0 && servicesInfo.staff.some(member => member.active)
        );

        if (!shouldAutoLoad) {
            return;
        }

        setMarketplaces(prev =>
            prev.map(marketplace => {
                const servicesInfo = organizationServicesInfo.find(
                    info => info.marketplace.id === marketplace.marketplaceId
                );

                if (!servicesInfo || marketplace.staffIds.length > 0) {
                    return marketplace;
                }

                return {
                    ...marketplace,
                    staffIds: servicesInfo.staff.filter(member => member.active).map(member => member.id)
                };
            })
        );
    }, [organizationServicesInfo, selectedMarketplaces]);

    const marketplaceStaffSelectionChangeHandler =
        (marketplaceId: number) =>
        (elements: Array<Element<number>>): void => {
            if (elements.length === 0) {
                dispatch(showError('The service must have at least one staff assigned.'));
                return;
            }

            setMarketplaces(prev =>
                prev.map(marketplace =>
                    marketplace.marketplaceId === marketplaceId
                        ? { ...marketplace, staffIds: elements.map(element => element.id) }
                        : marketplace
                )
            );
        };

    const toggleMarketplaceExpanded = (marketplaceId: number) => {
        setExpandedMarketplaceIds(prev =>
            prev.includes(marketplaceId) ? prev.filter(id => id !== marketplaceId) : [...prev, marketplaceId]
        );
    };

    const isValid = useMemo(() => {
        if (!hasActiveStaffInOrganization || selectedMarketplaces.length === 0) {
            return false;
        }

        return selectedMarketplaces.every(({ serviceMarketplace, servicesInfo }) => {
            const activeMemberIds = new Set(
                servicesInfo.staff.filter(member => member.active).map(member => member.id)
            );
            return serviceMarketplace.staffIds.some(staffId => activeMemberIds.has(staffId));
        });
    }, [hasActiveStaffInOrganization, selectedMarketplaces]);

    const handleComplete = () => {
        if (!isValid) {
            return;
        }

        onComplete(buildServiceStaffFromMarketplaces(marketplaces, organizationServicesInfo), marketplaces);
    };

    return (
        <div className={styles.container}>
            {hasActiveStaffInOrganization ? (
                <div className={styles.staffContainer}>
                    <p className={styles.title}>Add Staff Members</p>
                    <p className={styles.sectionTitle}>Choose staff members for each location</p>
                    {selectedMarketplaces.map(({ serviceMarketplace, servicesInfo }) => {
                        const activeMembers = servicesInfo.staff.filter(member => member.active);
                        const activeMemberIds = new Set(activeMembers.map(member => member.id));
                        const selectedCount = serviceMarketplace.staffIds.filter(staffId =>
                            activeMemberIds.has(staffId)
                        ).length;
                        const totalCount = activeMembers.length;
                        const isExpanded = expandedMarketplaceIds.includes(serviceMarketplace.marketplaceId);

                        return (
                            <div
                                key={serviceMarketplace.marketplaceId}
                                className={styles.marketplaceStaffSection}
                            >
                                <div
                                    className={styles.marketplaceHeader}
                                    onClick={() => toggleMarketplaceExpanded(serviceMarketplace.marketplaceId)}
                                >
                                    <div className={styles.marketplaceHeaderLeft}>
                                        <p className={styles.marketplaceName}>{servicesInfo.marketplace.name}</p>
                                        <p className={styles.marketplaceCount}>
                                            ({selectedCount}/{totalCount})
                                        </p>
                                    </div>
                                    <FontAwesomeIcon
                                        icon={isExpanded ? faChevronUp : faChevronDown}
                                        className={styles.marketplaceChevron}
                                    />
                                </div>
                                <div className={styles.marketplaceNameDivider} />
                                {isExpanded && (
                                    <div className={styles.marketplaceStaffContent}>
                                        {activeMembers.length > 0 ? (
                                            <ChipSelector
                                                id={`training_staff_selector_${serviceMarketplace.marketplaceId}`}
                                                searchLabel="Search Staff"
                                                allLabel="All Members"
                                                elements={buildStaffElements(servicesInfo.staff)}
                                                selectedElements={buildSelectedStaffElements(
                                                    servicesInfo.staff,
                                                    serviceMarketplace.staffIds
                                                )}
                                                changeSelection={marketplaceStaffSelectionChangeHandler(
                                                    serviceMarketplace.marketplaceId
                                                )}
                                            />
                                        ) : (
                                            <p className={styles.staffNotDefined}>Staff not defined</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className={styles.noneStaffContainer}>
                    <p className={styles.title}>No Staff members have been created yet.</p>
                    <p className={styles.noneStaffDescription}>
                        You can come back to add staff once you create them to activate the service.
                    </p>
                </div>
            )}

            <div className={styles.footer}>
                <Button
                    label="Save"
                    variant="primary"
                    size="large"
                    disabled={!isValid || isLoading}
                    onClick={handleComplete}
                />
            </div>
        </div>
    );
};

export default TrainingStaffStep;
